import { installChunkScriptRetry } from './utilities/chunk-script-retry.util';
import { importWithPendingRetry } from './utilities/lazy-with-retry.util';
import { StrictMode, Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import ErrorBoundary from './components/ErrorBoundary';
import AppLoading from './components/loading/app-loading';
import './styles/globals.css';
import './styles/override.css';
import {
  clearChunkReloadFlag,
  isChunkLoadError,
  reloadOnceForChunkError,
} from './utilities/chunk-load-error.util';

installChunkScriptRetry();
window.setTimeout(installChunkScriptRetry, 0);

const AppProviderWrapper = lazy(() =>
  importWithPendingRetry(() => import('./app/AppProviderWrapper')),
);


if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.getRegistrations().then((registrations) =>
      Promise.all(registrations.map((registration) => registration.unregister())),
    );
  });
}

// Suppress specific noisy React/AntD table warnings that are safe to ignore
const originalConsoleError = console.error;
console.error = (...args: any[]) => {
  const message = args[0];

  if (typeof message === 'string') {
    if (
      message.includes(
        '`expandedRowRender` should not use with nested Table',
      ) ||
      message.includes(
        '`expandIconColumnIndex` is deprecated. Please use `Table.EXPAND_COLUMN` in `columns` instead.',
      )
    ) {
      return;
    }
  }

  originalConsoleError(...args);
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);

let hasReloadedForChunkError = false;

// Handle missing JS chunks (script load errors)
window.addEventListener('error', (event) => {
  if (hasReloadedForChunkError) {
    return;
  }

  const target = event?.target as HTMLElement | null;
  if (target?.tagName === 'SCRIPT') {
    if (reloadOnceForChunkError()) {
      hasReloadedForChunkError = true;
    }
    return;
  }

  const error = (event as ErrorEvent).error ?? event.message;
  if (isChunkLoadError(error) && reloadOnceForChunkError()) {
    hasReloadedForChunkError = true;
    return;
  }

  console.error('Global error caught:', (event as ErrorEvent).error || event);
});

// Catch unhandled promise rejections (e.g. dynamic import ChunkLoadError)
window.addEventListener('unhandledrejection', (event) => {
  if (!hasReloadedForChunkError && isChunkLoadError(event.reason)) {
    hasReloadedForChunkError = true;
    console.warn('Chunk load failed (promise rejection), reloading...');
    if (reloadOnceForChunkError()) {
      return;
    }
  }

  console.error('Unhandled promise rejection:', event.reason);
});

clearChunkReloadFlag();

try {
  root.render(
    <StrictMode>
        <ErrorBoundary>
          <Suspense fallback={<AppLoading />}>
            <AppProviderWrapper />
          </Suspense>
        </ErrorBoundary>
    </StrictMode>,
  );
} catch (error) {
  console.error('Failed to render app:', error);
  document.body.innerHTML = `
    <div style="padding: 20px; font-family: monospace;">
      <h1>Application Error</h1>
      <p>Failed to initialize the application. Check the console for details.</p>
      <pre>${error}</pre>
    </div>
  `;
}
